import { bytesEqual, bytesToHex, concatBytes, hexToBytes, rootFromPath } from "./merkle.js";
const utf8 = (text) => new TextEncoder().encode(text);
/** The preimage, parsed and shape-checked. Throws on anything but the one shape. */
export function parsePreimage(text) {
    const parsed = JSON.parse(text);
    if (typeof parsed !== "object" ||
        parsed === null ||
        typeof parsed.fields !== "object" ||
        parsed.fields === null) {
        throw new Error("not a witness preimage");
    }
    const candidate = parsed;
    for (const [name, hash] of Object.entries(candidate.fields)) {
        if (typeof hash !== "string" || !/^[0-9a-f]{64}$/.test(hash)) {
            throw new Error(`field ${name} does not carry a hash`);
        }
    }
    return candidate;
}
export async function verifyLeaf(proof, sha256) {
    if (proof.salt === null)
        return { kind: "shredded" };
    let preimage;
    try {
        preimage = parsePreimage(proof.preimage);
    }
    catch (error) {
        return { kind: "failed", reason: error instanceof Error ? error.message : String(error) };
    }
    // 1. Every revealed field.
    const fields = [];
    for (const [name, revealed] of Object.entries(proof.revealed ?? {})) {
        const expected = preimage.fields[name];
        if (expected === undefined) {
            return { kind: "failed", reason: "a revealed field is not in the preimage", field: name };
        }
        const got = await sha256(concatBytes(hexToBytes(revealed.salt), utf8(revealed.text)));
        if (bytesToHex(got) !== expected) {
            return { kind: "failed", reason: "a revealed value does not match its fingerprint", field: name };
        }
        fields.push(name);
    }
    fields.sort();
    // 2. The preimage under its salt.
    const leaf = await sha256(concatBytes(hexToBytes(proof.salt), utf8(proof.preimage)));
    if (!bytesEqual(leaf, hexToBytes(proof.leafHash))) {
        return { kind: "failed", reason: "the preimage does not hash to the leaf" };
    }
    // 3. The path, once there is one.
    if (proof.path === null || proof.root === null) {
        return { kind: "awaiting-close", fields };
    }
    let climbed;
    try {
        climbed = await rootFromPath(leaf, proof.path, sha256);
    }
    catch (error) {
        return { kind: "failed", reason: error instanceof Error ? error.message : String(error) };
    }
    if (!bytesEqual(climbed, hexToBytes(proof.root))) {
        return { kind: "failed", reason: "the path does not climb to the hour's root" };
    }
    return { kind: "verified", fields, anchoredRoot: proof.root };
}
//# sourceMappingURL=verify.js.map